import { FormEvent } from "react"
import { useForm } from "@inertiajs/react"

import { Button } from "@/components/ui/button"
import { FieldError } from "@/components/ui/field-error"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { PROMOTION_PATHS, tPromotion } from "@/features/staff/promotions/promotions"

type Props = {
  promotionId: number
  onDone?: () => void
}

type FormShape = {
  code: string
  expires_at: string
  max_redemptions: string
}

/**
 * Minting a code.
 *
 * **Only three decisions live here, and none of them is money.** A coupon does
 * not define an amount — it points at the promotion that does. Letting this form
 * set a value would mean two places to change a campaign and two chances for
 * them to disagree, so what a manager chooses here is who may still use the
 * code, how many times, and until when.
 *
 * **The code is upcased as it is typed**, matching `Location::Coupon`'s own
 * `normalize_code`. The server normalizes regardless; doing it in the field is
 * what keeps the uniqueness refusal from arriving as a surprise about a code the
 * manager believes is different from one already minted.
 *
 * **There is no edit form for the code itself.** The controller drops `code` on
 * update on purpose: changing the string on a live coupon silently retires
 * whatever is already printed on a flyer while the row's history claims
 * otherwise. A wrong code is revoked and re-minted.
 */
export function CouponForm({ promotionId, onDone }: Props) {
  const { data, setData, post, errors, processing } = useForm<FormShape>({
    code: "",
    expires_at: "",
    max_redemptions: "1",
  })

  // `record.errors.to_hash(true)` keys a model-level error under `base`, which
  // is not a field on this form and so is not in `useForm`'s inferred error map.
  // Dropping it would swallow every refusal that is not about one input —
  // `promotion_belongs_to_the_location`, for one.
  const baseError = (errors as Record<string, string | string[] | undefined>).base

  const complete = data.code.trim().length > 0 && Number(data.max_redemptions) > 0

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (!complete) return

    post(PROMOTION_PATHS.createCoupon(promotionId), {
      preserveScroll: true,
      onSuccess: () => onDone?.(),
    })
  }

  const testId = `coupon-new-${promotionId}`

  return (
    <form onSubmit={submit} className="space-y-4" data-testid={`${testId}-form`}>
      <FieldError error={baseError} />

      <div className="space-y-2">
        <Label htmlFor={`${testId}-code`}>{tPromotion("coupon_code_label")}</Label>
        <Input
          id={`${testId}-code`}
          value={data.code}
          onChange={(event) => setData("code", event.target.value.toUpperCase())}
          placeholder={tPromotion("coupon_code_placeholder")}
          autoComplete="off"
          spellCheck={false}
          className="font-mono uppercase"
          data-testid={`${testId}-code`}
          required
        />
        <FieldError error={errors.code} />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor={`${testId}-expires-at`}>{tPromotion("coupon_expires_label")}</Label>
          <Input
            id={`${testId}-expires-at`}
            type="datetime-local"
            value={data.expires_at}
            onChange={(event) => setData("expires_at", event.target.value)}
            className="tabular-nums"
            data-testid={`${testId}-expires-at`}
          />
          <p className="text-muted-foreground text-xs">{tPromotion("coupon_expires_hint")}</p>
          <FieldError error={errors.expires_at} />
        </div>

        <div className="space-y-2">
          <Label htmlFor={`${testId}-max-redemptions`}>{tPromotion("coupon_max_label")}</Label>
          <Input
            id={`${testId}-max-redemptions`}
            type="number"
            min="1"
            step="1"
            value={data.max_redemptions}
            onChange={(event) => setData("max_redemptions", event.target.value)}
            className="tabular-nums"
            data-testid={`${testId}-max-redemptions`}
            required
          />
          <FieldError error={errors.max_redemptions} />
        </div>
      </div>

      <div className="flex items-center justify-end gap-2">
        {onDone && (
          <Button type="button" variant="outline" onClick={onDone} data-testid={`${testId}-cancel`}>
            {tPromotion("coupon_cancel")}
          </Button>
        )}
        <Button type="submit" disabled={processing || !complete} data-testid={`${testId}-submit`}>
          {tPromotion("coupon_create")}
        </Button>
      </div>
    </form>
  )
}
